import { useRef } from "react";
import { Outlet } from "react-router";

import { Copyright, Eye, Palette, PencilSimple, PlusCircle } from "@phosphor-icons/react";
import { useBreakpoint } from "@reactive-resume/hooks";
import { Button, ScrollArea, Separator } from "@reactive-resume/ui";

import * as Tabs from "@radix-ui/react-tabs";

import { useCoverLetterStore } from "@/client/stores/cover-letter";
import { CoverLetterBuilderHeader } from "../../_components/header";
import { CoverLetterBuilderToolbar } from "../../_components/toolbar";

import { SenderSection } from "./sections/sender";
import { RecipientSection } from "./sections/recipient";
import { ReferenceSection } from "./sections/reference";
import { LetterContentSection } from "./sections/letter-content";
import { CLCustomSection } from "./sections/custom";

import { CLTemplateSection } from "./sections/format/template";
import { CLLayoutSection } from "./sections/format/layout";
import { CLTypographySection } from "./sections/format/typography";
import { CLThemeSection } from "./sections/format/theme";
import { CLCssSection } from "./sections/format/css";
import { CLPageSection } from "./sections/format/page";
import { CLExportSection } from "./sections/format/export";
import { CLImportSection } from "./sections/format/import";
import { CLNotesSection } from "./sections/format/notes";

export const CoverLetterLeftSidebar = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isDesktop } = useBreakpoint();

  const addSection = useCoverLetterStore((state) => state.addSection);
  const custom = useCoverLetterStore((state) => state.coverLetter?.data?.custom || {});
  const customSections = Object.entries(custom);

  return (
    <Tabs.Root defaultValue="content" className="h-full w-full p-4">
      <Tabs.List className="sticky top-0 z-10 mx-auto mb-4 flex w-max gap-2 rounded-full border bg-gray-200/90 px-2 py-1 shadow-inner">
        <Tabs.Trigger
          value="content"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-transparent px-3 py-1.5 text-xs font-medium ring-offset-background transition-all hover:text-primary data-[state=active]:border-2 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
        >
          <PencilSimple size={16} />
          Edit Content
        </Tabs.Trigger>
        <Tabs.Trigger
          value="format"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-transparent px-3 py-1.5 text-xs font-medium ring-offset-background transition-all hover:text-primary data-[state=active]:border-2 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
        >
          <Palette size={16} />
          Edit Format
        </Tabs.Trigger>
        {!isDesktop && (
          <Tabs.Trigger
            value="preview"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-transparent px-3 py-1.5 text-xs font-medium ring-offset-background transition-all hover:text-primary data-[state=active]:border-2 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-md"
          >
            <Eye size={16} />
            Preview
          </Tabs.Trigger>
        )}
      </Tabs.List>

      {/* Edit Content tab */}
      <Tabs.Content value="content" className="h-full w-full">
        <CoverLetterBuilderHeader />
        <ScrollArea
          type="scroll"
          orientation="vertical"
          className="force-scrollbar-visible h-[calc(100vh-5.2rem)] flex-1"
        >
          <div ref={containerRef} className="grid gap-y-4 rounded-lg @container/left md:mx-4">
            <SenderSection />
            <RecipientSection />
            <ReferenceSection />
            <LetterContentSection />

            {/* Custom Sections */}
            {customSections.map(([id]) => (
              <CLCustomSection key={id} id={id} />
            ))}

            <Button size="lg" variant="outline" className="w-full" onClick={addSection}>
              <PlusCircle />
              <span className="ml-2">Add a new section</span>
            </Button>
          </div>
        </ScrollArea>
      </Tabs.Content>

      {/* Edit Format tab */}
      <Tabs.Content value="format" className="h-full w-full">
        <CoverLetterBuilderHeader />
        <ScrollArea
          type="scroll"
          orientation="vertical"
          className="force-scrollbar-visible h-[calc(100vh-5.2rem)] flex-1"
        >
          <div className="grid gap-y-5 rounded-lg @container/right md:mx-4">
            <CLTemplateSection />
            <CLLayoutSection />
            <CLTypographySection />
            <CLThemeSection />
            <CLCssSection />
            <CLPageSection />
            <CLExportSection />
            <CLImportSection />
            <CLNotesSection />
            <Separator />
            <Copyright className="text-center" />
          </div>
        </ScrollArea>
      </Tabs.Content>

      {/* Preview tab - mobile only */}
      {!isDesktop && (
        <Tabs.Content value="preview" className="h-full w-full px-2">
          <CoverLetterBuilderHeader />
          <div className="absolute inset-0 flex items-center justify-center">
            <Outlet />
          </div>
          <div className="flex w-full items-center justify-center">
            <CoverLetterBuilderToolbar />
          </div>
        </Tabs.Content>
      )}
    </Tabs.Root>
  );
};
